import { Link, useLocation, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { AnnouncementPageHeader } from "../components/announcements/AnnouncementPageHeader";
import { AnnouncementRichText } from "../components/announcements/AnnouncementRichText";
import { useAnnouncement } from "../hooks/useAnnouncements";
import { Skeleton } from "../components/ui/skeleton";
import { formatAnnouncementDate } from "@/utils/formatAnnouncementDate";

function AnnouncementDetailSkeleton() {
  return (
    <div className="mt-8 space-y-6">
      <div className="space-y-3">
        <Skeleton className="h-6 w-3/5" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-11/12" />
        <Skeleton className="h-4 w-4/5" />
      </div>
      <Skeleton className="h-40 w-full rounded-xl" />
      <div className="space-y-3">
        <Skeleton className="h-5 w-2/5" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-10/12" />
        <Skeleton className="h-4 w-8/12" />
      </div>
    </div>
  );
}

export function AnnouncementDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const { announcement, loading } = useAnnouncement(id);
  const navigationTitle =
    typeof location.state === "object" &&
    location.state !== null &&
    "title" in location.state &&
    typeof location.state.title === "string"
      ? location.state.title
      : null;
  const navigationUpdatedAt =
    typeof location.state === "object" &&
    location.state !== null &&
    "updatedAt" in location.state &&
    typeof location.state.updatedAt === "number"
      ? location.state.updatedAt
      : null;
  const pageTitle = announcement?.title ?? navigationTitle ?? t("announcements.listTitle");
  const pageSubtitle =
    announcement?.updatedAt || navigationUpdatedAt
      ? formatAnnouncementDate(announcement?.updatedAt ?? navigationUpdatedAt ?? 0)
      : undefined;

  if (loading) {
    return (
      <div className="space-y-6">
        <AnnouncementPageHeader
          backTo="/announcements"
          title={pageTitle}
          subtitle={pageSubtitle}
        />
        <AnnouncementDetailSkeleton />
      </div>
    );
  }

  if (!announcement) {
    return (
      <div className="space-y-6">
        <AnnouncementPageHeader
          backTo="/announcements"
          title={pageTitle}
          subtitle={pageSubtitle}
        />
        <div className="text-center py-12">
          <p className="text-slate-500 dark:text-slate-400">
            {t("announcements.notFound")}
          </p>
          <Link
            to="/announcements"
            className="mt-4 inline-block text-emerald-600 dark:text-[#00FFA3] hover:underline text-sm font-medium"
          >
            {t("announcements.backToList")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AnnouncementPageHeader
        backTo="/announcements"
        title={pageTitle}
        subtitle={pageSubtitle}
      />
      <AnnouncementRichText html={announcement.content} className="mt-8" />
    </div>
  );
}
